<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CustomerContextAPI from '../../../../api/integrations/customerContext';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const props = defineProps({
  connected: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const isLoadingStatus = ref(true);
const statusError = ref('');
const stripeStatus = ref({ connected: false });
const posthogStatus = ref({ connected: false });

const projectId = ref('');
const host = ref('https://eu.posthog.com');
const isConnectingPostHog = ref(false);
const isDisconnectingPostHog = ref(false);
const posthogFormError = ref('');

const secretKey = ref('');
const isConnectingStripe = ref(false);
const isDisconnectingStripe = ref(false);
const stripeFormError = ref('');

const fetchStatus = async () => {
  try {
    isLoadingStatus.value = true;
    statusError.value = '';
    const { data } = await CustomerContextAPI.getIntegrations();
    stripeStatus.value = data.stripe;
    posthogStatus.value = data.posthog;
  } catch (error) {
    statusError.value =
      error.response?.data?.error ||
      t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STATUS_ERROR');
  } finally {
    isLoadingStatus.value = false;
  }
};

onMounted(fetchStatus);

const handleConnectPostHog = async () => {
  if (!projectId.value || !host.value) {
    posthogFormError.value = t(
      'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.MISSING_FIELDS'
    );
    return;
  }
  try {
    posthogFormError.value = '';
    isConnectingPostHog.value = true;
    const { data } = await CustomerContextAPI.connectPostHog({
      projectId: projectId.value,
      host: host.value,
    });
    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    }
  } catch (error) {
    posthogFormError.value = error.response?.data?.error || error.message;
  } finally {
    isConnectingPostHog.value = false;
  }
};

const handleDisconnectPostHog = async () => {
  try {
    posthogFormError.value = '';
    isDisconnectingPostHog.value = true;
    await CustomerContextAPI.disconnectPostHog();
    await fetchStatus();
  } catch (error) {
    posthogFormError.value = error.response?.data?.error || error.message;
  } finally {
    isDisconnectingPostHog.value = false;
  }
};

const handleConnectStripe = async () => {
  if (!secretKey.value) {
    stripeFormError.value = t(
      'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.MISSING_FIELDS'
    );
    return;
  }
  try {
    stripeFormError.value = '';
    isConnectingStripe.value = true;
    await CustomerContextAPI.connectStripe({ secretKey: secretKey.value });
    secretKey.value = '';
    await fetchStatus();
  } catch (error) {
    stripeFormError.value = error.response?.data?.error || error.message;
  } finally {
    isConnectingStripe.value = false;
  }
};

const handleDisconnectStripe = async () => {
  try {
    stripeFormError.value = '';
    isDisconnectingStripe.value = true;
    await CustomerContextAPI.disconnectStripe();
    await fetchStatus();
  } catch (error) {
    stripeFormError.value = error.response?.data?.error || error.message;
  } finally {
    isDisconnectingStripe.value = false;
  }
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-8 max-w-lg">
        <div
          v-if="props.connected === 'true'"
          class="p-4 rounded-md bg-n-teal-3 text-n-teal-12 text-sm"
        >
          {{
            $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.CONNECT_SUCCESS')
          }}
        </div>
        <div
          v-else-if="props.connected === 'false'"
          class="p-4 rounded-md bg-n-ruby-3 text-n-ruby-12 text-sm"
        >
          {{
            $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.CONNECT_FAILED')
          }}
        </div>
        <p v-if="statusError" class="text-sm text-n-ruby-9">
          {{ statusError }}
        </p>

        <!-- Stripe -->
        <div class="flex flex-col gap-4">
          <div>
            <h4 class="text-n-slate-12 text-sm font-medium">
              {{ $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.TITLE') }}
            </h4>
            <p class="text-n-slate-11 text-sm">
              {{
                $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.DESCRIPTION')
              }}
            </p>
          </div>

          <div
            v-if="!isLoadingStatus && stripeStatus.connected"
            class="flex items-center justify-between p-3 rounded-md bg-n-slate-2"
          >
            <span class="text-sm text-n-slate-12">
              {{
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.CONNECTED_HINT',
                  {
                    hint: stripeStatus.hint,
                  }
                )
              }}
            </span>
            <Button
              variant="outline"
              color="ruby"
              size="sm"
              :label="
                $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.DISCONNECT_BUTTON')
              "
              :is-loading="isDisconnectingStripe"
              @click="handleDisconnectStripe"
            />
          </div>
          <template v-else-if="!isLoadingStatus">
            <Input
              v-model="secretKey"
              type="password"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.SECRET_KEY_LABEL'
                )
              "
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.SECRET_KEY_PLACEHOLDER'
                )
              "
              :message="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.SECRET_KEY_HELP'
                )
              "
              :message-type="stripeFormError ? 'error' : 'info'"
            />
            <p v-if="stripeFormError" class="text-sm text-n-ruby-9">
              {{ stripeFormError }}
            </p>
            <Button
              class="self-start"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.STRIPE.CONNECT_BUTTON'
                )
              "
              :is-loading="isConnectingStripe"
              @click="handleConnectStripe"
            />
          </template>
        </div>

        <!-- PostHog -->
        <div class="flex flex-col gap-4">
          <div>
            <h4 class="text-n-slate-12 text-sm font-medium">
              {{ $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.TITLE') }}
            </h4>
            <p class="text-n-slate-11 text-sm">
              {{
                $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.DESCRIPTION')
              }}
            </p>
          </div>

          <div
            v-if="!isLoadingStatus && posthogStatus.connected"
            class="flex items-center justify-between p-3 rounded-md bg-n-slate-2"
          >
            <span class="text-sm text-n-slate-12">
              {{
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.CONNECTED_HINT',
                  {
                    projectId: posthogStatus.projectId,
                    host: posthogStatus.host,
                  }
                )
              }}
            </span>
            <Button
              variant="outline"
              color="ruby"
              size="sm"
              :label="
                $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.DISCONNECT_BUTTON')
              "
              :is-loading="isDisconnectingPostHog"
              @click="handleDisconnectPostHog"
            />
          </div>
          <template v-else-if="!isLoadingStatus">
            <Input
              v-model="projectId"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.PROJECT_ID_LABEL'
                )
              "
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.PROJECT_ID_PLACEHOLDER'
                )
              "
            />
            <Input
              v-model="host"
              :label="
                $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.HOST_LABEL')
              "
              :message="
                $t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.HOST_HELP')
              "
              :message-type="posthogFormError ? 'error' : 'info'"
            />
            <p v-if="posthogFormError" class="text-sm text-n-ruby-9">
              {{ posthogFormError }}
            </p>
            <Button
              class="self-start"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.POSTHOG.CONNECT_BUTTON'
                )
              "
              :is-loading="isConnectingPostHog"
              @click="handleConnectPostHog"
            />
          </template>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
